import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { SearchProductsQuery } from '../../../common/generated-types';
import { getRouteArrayParam } from '../../../common/utils/get-route-array-param';

export interface FacetWithValues {
    id: string;
    name: string;
    values: Array<{
        id: string;
        name: string;
        count: number;
    }>;
}

export interface FacetFilters {
    [facetId: string]: string[];
}
@Component({
    selector: 'vsf-product-list-controls',
    templateUrl: './product-list-controls.component.html',
    // styleUrls: ['./product-list-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListControlsComponent implements OnChanges {
    @Input() activeFacetValueIds: FacetFilters = {};
    @Input() facetValues: SearchProductsQuery['search']['facetValues'] | null;
    @Input() totalResults = 0;
    facets: FacetWithValues[];

    constructor(private route: ActivatedRoute, private router: Router) {
    }

    get filtersExpanded(): boolean {
        return Object.keys(this.activeFacetValueIds).some(key => this.activeFacetValueIds[key]?.length > 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('facetValues' in changes) {
            this.facets = this.groupFacetValues(this.facetValues);
        }
    }

    isActive(facetValueId: string): boolean {
        return Object.values(this.activeFacetValueIds).some(values => values.includes(facetValueId));
    }

    toggleFacetValueIdInRoute(facetId: string, id: string) {
        this.toggleFacetValueId(facetId, id);
        const queryParams: { [param: string]: string } = {};
        const facetIds = Object.keys(this.activeFacetValueIds);
        if (facetIds.length > 0) {
            const facetStrings = facetIds.map(id => {return `${id}:${this.activeFacetValueIds[id].join(',')}`});
            queryParams['facets'] = facetStrings.join(';');
        } else {
            queryParams['facets'] = ''; // Clear the facet parameter if no active facets
        }
        this.router.navigate([], {
            queryParams: queryParams,
            queryParamsHandling: 'merge',
            relativeTo: this.route,
            state: {
                noScroll: true,
            },
        });
    }

    toggleFacetValueId(facetId: string, id: string): void {
        const currentValues = this.activeFacetValueIds[facetId] || [];
        const index = currentValues.indexOf(id);
        if (index > -1) {
            // Remove the id if it's already in the array
            currentValues.splice(index, 1);
        } else {
            // Add the id if it's not in the array
            currentValues.push(id);
        }
        if (currentValues.length > 0) {
            this.activeFacetValueIds[facetId] = currentValues;
        } else {
            delete this.activeFacetValueIds[facetId]; // Remove the property if no values
        }
    }

    trackById(index: number, item: { id: string }) {
        return item.id;
    }

    private groupFacetValues(facetValues: SearchProductsQuery['search']['facetValues'] | null): FacetWithValues[] {
        if (!facetValues) {
            return [];
        }
        const activeFacetValueIds = this.activeFacetValueIds;
        const facetMap = new Map<string, FacetWithValues>();
        for (const { count, facetValue: { id, name, facet } } of facetValues) {
            if (count === this.totalResults && !Object.values(activeFacetValueIds).some(values => values.includes(id))) {
                // skip FacetValues that do not have any effect on the
                // result set and are not active
                continue;
            }
            const facetFromMap = facetMap.get(facet.id);
            if (facetFromMap) {
                facetFromMap.values.push({ id, name, count });
            } else {
                facetMap.set(facet.id, { id: facet.id, name: facet.name, values: [{ id, name, count }]});
            }
        }
        return Array.from(facetMap.values());
    }
}
